'use client';
import { User, Wifi, AlertCircle, Loader2 } from 'lucide-react';
import { useCustomerByPhone } from '@/hooks/voice/use-customer-by-phone';
import { cn } from '@/lib/utils';

interface Props {
  phone: string;
  /** layout compacto pra usar dentro do panel (sem header). default = false */
  compact?: boolean;
}

function fmtMoney(v: number): string {
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export function CustomerCard({ phone, compact }: Props) {
  const { customer, loading, error } = useCustomerByPhone(phone);

  if (loading) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Buscando cliente…
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-start gap-2 px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
        <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
        Erro ao buscar cliente
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground">
        <User className="h-3.5 w-3.5" />
        Não cadastrado no ERP
      </div>
    );
  }

  const hasFaturas = customer.faturas && customer.faturas.abertas > 0;

  return (
    <div
      className={cn(
        'rounded-lg border bg-card',
        compact ? 'p-2.5' : 'p-3',
        hasFaturas ? 'border-orange-500/40 bg-orange-500/5' : 'border-border',
      )}
    >
      <div className="flex items-start gap-2.5">
        <div
          className={cn(
            'flex shrink-0 items-center justify-center rounded-full',
            compact ? 'h-8 w-8' : 'h-10 w-10',
            hasFaturas
              ? 'bg-orange-500/20 text-orange-600 dark:text-orange-400'
              : 'bg-primary/10 text-primary',
          )}
        >
          <User className={compact ? 'h-4 w-4' : 'h-5 w-5'} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <p className={cn('font-medium truncate', compact ? 'text-sm' : 'text-base')}>
              {customer.nome}
            </p>
            {hasFaturas && (
              <span className="rounded bg-orange-500/20 px-1.5 py-0.5 text-[10px] font-medium text-orange-700 dark:text-orange-300">
                {customer.faturas!.abertas}× em aberto
              </span>
            )}
          </div>
          {customer.cpf && (
            <p className="text-[11px] text-muted-foreground font-mono">{customer.cpf}</p>
          )}
        </div>
      </div>

      {(customer.plano || customer.conexao || customer.endereco) && !compact && (
        <div className="mt-2 space-y-1 border-t border-border/50 pt-2 text-xs">
          {customer.plano && (
            <div className="flex items-center gap-1.5 text-muted-foreground">
              <Wifi className="h-3 w-3" />
              <span className="truncate">{customer.plano}</span>
            </div>
          )}
          {customer.diaVencimento != null && (
            <p className="text-muted-foreground">
              Vencimento dia {customer.diaVencimento}
            </p>
          )}
          {hasFaturas && (
            <p className="text-orange-700 dark:text-orange-400 font-medium">
              Em aberto: {fmtMoney(customer.faturas!.valorTotal)}
            </p>
          )}
          {customer.endereco && (
            <p className="text-muted-foreground truncate">{customer.endereco}</p>
          )}
        </div>
      )}
    </div>
  );
}
